// Available roles in the system
export const UserRoles = {
  ADMIN: 'ADMIN',
  DOCTOR: 'DOCTOR',
  NURSE: 'NURSE',
  PSYCHOLOGIST: 'PSYCHOLOGIST',
  NUTRITIONIST: 'NUTRITIONIST',
  SOCIAL_WORKER: 'SOCIAL_WORKER',
  RECEPTIONIST: 'RECEPTIONIST',
  PHARMACIST: 'PHARMACIST',
  PHYSICAL_THERAPIST: 'PHYSICAL_THERAPIST',
  OCCUPATIONAL_THERAPIST: 'OCCUPATIONAL_THERAPIST',
  COORDINATOR: 'COORDINATOR',
} as const;

export type UserRole = (typeof UserRoles)[keyof typeof UserRoles];

export const RoleLabels: Record<string, string> = {
  ADMIN: 'Administrador',
  DOCTOR: 'Médico(a)',
  NURSE: 'Enfermeiro(a)',
  PSYCHOLOGIST: 'Psicólogo(a)',
  NUTRITIONIST: 'Nutricionista',
  SOCIAL_WORKER: 'Assistente Social',
  RECEPTIONIST: 'Recepcionista',
  PHARMACIST: 'Farmacêutico(a)',
  PHYSICAL_THERAPIST: 'Fisioterapeuta',
  OCCUPATIONAL_THERAPIST: 'Terapeuta Ocupacional',
  COORDINATOR: 'Coordenador(a) de Cuidado',
};

// Available permissions in the system
export const Permissions = {
  // Patient permissions
  PATIENTS_READ: 'patients:read',
  PATIENTS_WRITE: 'patients:write',
  PATIENTS_DELETE: 'patients:delete',
  // Clinical permissions
  CLINICAL_READ: 'clinical:read',
  CLINICAL_WRITE: 'clinical:write',
  // Risk permissions
  RISK_READ: 'risk:read',
  RISK_WRITE: 'risk:write',
  // Care line permissions
  CARELINES_READ: 'carelines:read',
  CARELINES_WRITE: 'carelines:write',
  // Questionnaire permissions
  QUESTIONNAIRES_READ: 'questionnaires:read',
  QUESTIONNAIRES_WRITE: 'questionnaires:write',
  // Demand permissions
  DEMANDS_READ: 'demands:read',
  DEMANDS_WRITE: 'demands:write',
  // Task permissions
  TASKS_READ: 'tasks:read',
  TASKS_WRITE: 'tasks:write',
  // Message permissions
  MESSAGES_READ: 'messages:read',
  MESSAGES_WRITE: 'messages:write',
  // Report permissions
  REPORTS_READ: 'reports:read',
  // Admin permissions
  USERS_READ: 'users:read',
  USERS_WRITE: 'users:write',
  SETTINGS_READ: 'settings:read',
  SETTINGS_WRITE: 'settings:write',
} as const;

export type Permission = (typeof Permissions)[keyof typeof Permissions];

export interface User {
  id: string;
  email: string;
  name: string;
  professionalId?: string;
  patientId?: string; // For portal users
  roles: UserRole[];
  permissions: Permission[];
  createdAt: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

// User list response (lighter than full User)
export interface UserListItem {
  id: string;
  email: string;
  name: string;
  professionalId?: string;
  phone?: string;
  roles: string[];
  active: boolean;
  createdAt: string;
}

// Request types for user management
export interface CreateUserRequest {
  email: string;
  name: string;
  password: string;
  professionalId?: string;
  phone?: string;
  roles: string[];
  active?: boolean;
}

export interface UpdateUserRequest {
  name: string;
  professionalId?: string;
  phone?: string;
  roles?: string[];
  active?: boolean;
}

export interface ChangePasswordRequest {
  newPassword: string;
}

// Role response from API
export interface RoleResponse {
  id: string;
  name: string;
  description: string;
}
